import { DiscountSettings, PlaceInit, WidgetSettings } from '@/interfaces/models/place';
import Timetable from '@/models/timetable';
import Image from '@/models/image';
import assetsUtils from '@/utils/url.utils';

export default class Place {
  public address: string;
  public appearance: any;
  public averageLeadTimeTakeAway: number;
  public averageVisitDuration: number;
  public autoAcceptanceOfOrdersEnabled: boolean;
  public comment: string | null;
  public country: string;
  public currency: string;
  public productLimit: number;
  public productSettings: any;
  public discountSettings: DiscountSettings;
  public emails: Array<string>;
  public embed: any;
  public highlightReservationBefore: number;
  public highlightTakeAwayBefore: number;
  public id: string;
  public image: Image = new Image();
  public isOrderNeedCooking: boolean;
  public isOnlineOrderWithPaymentEnabled: boolean;
  public isOnlineOrderWithoutPaymentEnabled: boolean;
  public isReservationTBDefault: boolean;
  public isShowAdditionalSettingsOfReservation: boolean;
  public isShowAdditionalSettingsOfTakeAway: boolean;
  public isShowAllOrders: boolean;
  public isCumulativeDiscountSystemIncluded: boolean = false;
  public isPrintOrderComment: boolean;
  public isTakeAwayTBDefault: boolean;
  public maxTimeReservation: number;
  public maxTimeTakeAway: number;
  public minTimeReservation: number;
  public minTimeTakeAway: number;
  public mobileAppInstallationQrCode: string;
  public mobileAppQrCode: string;
  public projectMobileAppQrCode: string;
  public name: string;
  public personLimit: number;
  public phones: number | Array<string>;
  public projectId: string;
  public purchasePercentageConvertedToPoints: number = 10;
  public receiptCharacterHeight: number;
  public reservationComment: string | null;
  public slug: string | null;
  public smsTitle: string;
  public showBlockedInformation: boolean;
  public showAlmostBlockedInformation: boolean;
  public tableLimit: number;
  public tableReservationLimit: number;
  public takeAwayComment: string | null;
  public timetable: any;
  public timetableReservation: any;
  public timetableTakeAway: any;
  public timezone: number;
  public token: string;
  public typeOfOrderNumbering: number;
  public widget: WidgetSettings;

  [key: string]: any;

  constructor(placeProps: PlaceInit) {
    this.address = placeProps.address;
    this.appearance = placeProps.appearance;
    this.averageLeadTimeTakeAway = placeProps.averageLeadTimeTakeAway;
    this.averageVisitDuration = placeProps.averageVisitDuration;
    this.autoAcceptanceOfOrdersEnabled = placeProps.autoAcceptanceOfOrdersEnabled;
    this.comment = placeProps.comment;
    this.country = placeProps.country;
    this.city = placeProps.city;
    this.currency = placeProps.currency;
    this.productLimit = placeProps.productLimit;
    this.productSettings = placeProps.productSettings;
    this.discountSettings = placeProps.discountSettings;
    this.emails = placeProps.emails;
    this.embed = placeProps.embed;
    this.highlightReservationBefore = placeProps.highlightReservationBefore;
    this.highlightTakeAwayBefore = placeProps.highlightTakeAwayBefore;
    this.id = placeProps.id;

    if (placeProps.image) {
      this.image = new Image(placeProps.image);
    }

    this.isOnlineOrderWithPaymentEnabled = placeProps.isOnlineOrderWithPaymentEnabled;
    this.isOnlineOrderWithoutPaymentEnabled = placeProps.isOnlineOrderWithoutPaymentEnabled;
    this.isOrderNeedCooking = placeProps.isOrderNeedCooking;
    this.isReservationTBDefault = placeProps.isReservationTBDefault;
    this.isShowAdditionalSettingsOfReservation = placeProps.isShowAdditionalSettingsOfReservation;
    this.isShowAdditionalSettingsOfTakeAway = placeProps.isShowAdditionalSettingsOfTakeAway;
    this.isShowAllOrders = placeProps.isShowAllOrders;
    this.isPrintOrderComment = placeProps.isPrintOrderComment;
    this.isTakeAwayTBDefault = placeProps.isTakeAwayTBDefault;
    this.maxTimeReservation = placeProps.maxTimeReservation;
    this.maxTimeTakeAway = placeProps.maxTimeTakeAway;
    this.minTimeReservation = placeProps.minTimeReservation;
    this.minTimeTakeAway = placeProps.minTimeTakeAway;
    this.mobileAppInstallationQrCode = placeProps.mobileAppInstallationQrCode;
    this.mobileAppQrCode = placeProps.mobileAppQrCode;
    this.projectMobileAppQrCode = placeProps.project.mobileAppQrCode;
    this.name = placeProps.name;
    this.personLimit = placeProps.personLimit;
    this.phones = placeProps.phones;
    this.projectId = placeProps.projectId;
    this.receiptCharacterHeight = placeProps.receiptCharacterHeight;
    this.reservationComment = placeProps.reservationComment;
    this.smsTitle = placeProps.smsTitle;
    this.slug = placeProps.slug;
    this.showBlockedInformation = placeProps.showBlockedInformation;
    this.showAlmostBlockedInformation = placeProps.showAlmostBlockedInformation;
    this.tableLimit = placeProps.tableLimit;
    this.tableReservationLimit = placeProps.tableReservationLimit;
    this.takeAwayComment = placeProps.takeAwayComment;
    this.timetable = placeProps.timetable.map((timetableProps: any) => new Timetable(timetableProps));
    this.timetableReservation = placeProps.timetableReservation.map((timetableProps: any) => new Timetable(timetableProps));
    this.timetableTakeAway = placeProps.timetableTakeAway.map((timetableProps: any) => new Timetable(timetableProps));
    this.timezone = placeProps.timezone;
    this.token = placeProps.token;
    this.typeOfOrderNumbering = placeProps.typeOfOrderNumbering;
    this.widget = placeProps.widget;
  }

  get preview(): URL {
    if (this.image.original && this.image.path) {
      return assetsUtils.generateAssetsUrl(this.image.path);
    }

    return require('../assets/images/header-logo.png');
  }

  mergeWithNewFields(placeProps: PlaceInit) {
    for (let key in placeProps) {
      if (placeProps.hasOwnProperty(key) && this.hasOwnProperty(key)) {
        switch (key) {
          case 'timetable':
          case 'timetableReservation':
          case 'timetableTakeAway': {
            this[key] = placeProps[key].map((timetableProps: any) => new Timetable(timetableProps));

            break;
          }

          default: {
            this[key] = placeProps[key];
          }
        }
      }
    }
  }
}
